import {
  Box,
  BoxProps,
  HStack,
  Heading,
  IconButton,
  Image,
  LinkOverlay,
  Text,
  VStack
} from "@chakra-ui/react"
import { forwardRef, useMemo } from "react"

import Link from "@pathwright/ux/src/components/link/Link"
import RevealOnGroupHover from "@pathwright/ux/src/components/style/RevealOnGroupHover"
import Pathicon from "../../../pathicon/Pathicon"
import useSoftArchiveRegistration from "../../registration/useSoftArchiveRegistration"
import { getRegistrationPathLink } from "../../utils"
import NestedPaths from "./NestedPaths"
import { PathCardClickTarget } from "./PathCard"
import PathCardAction from "./PathCardAction"
import PathCardRegistrationControls from "./PathCardRegistrationControls"
import { RegistrationPathCardProps } from "./types"

type PathCardListItemProps = RegistrationPathCardProps & BoxProps

const PathCardListItem = forwardRef<HTMLDivElement, PathCardListItemProps>(
  (
    {
      registration,
      parentRegistration,
      nestedRegistrations,
      enableLink,
      clickTargetProps,
      ...boxProps
    },
    ref
  ) => {
    const resource = registration.resource!
    const pathLink = useMemo(() => getRegistrationPathLink(registration), [])

    // Using image width also for determining offset of nested paths.
    const imgWidth = "4em"

    const [softArchive] = useSoftArchiveRegistration(registration)
    // You can only soft unarchive a child registration when it's parent
    // is not soft archived.
    const canSoftUnarchive =
      !!registration.softArchivedTime &&
      (!parentRegistration || !parentRegistration.softArchivedTime)

    return (
      <Box
        ref={ref}
        // Using "group" className to allow revealing of PathCardRegistrationControls
        // on hover of the PathCardListItem.
        className="PathCardListItem group"
        w="100%"
        pos="relative"
        {...boxProps}
        // Eh, overriding, which might not be best.
        borderRadius="md"
      >
        {clickTargetProps && (
          <PathCardClickTarget name={resource.name} {...clickTargetProps} />
        )}
        {enableLink && !clickTargetProps && (
          <LinkOverlay asChild>
            <Link
              className="PathCardListItem__link-overlay"
              to={pathLink}
              w="100%"
              textDecoration="none"
            />
          </LinkOverlay>
        )}
        <VStack
          w="100%"
          alignItems="flex-start"
          gap={1}
          css={{
            ["& .PathProgressBar"]: {
              fontSize: ".6em"
            }
          }}
        >
          <HStack w="100%">
            <Image
              src={resource.cover_image}
              w={imgWidth}
              h="3em"
              borderRadius="md"
              alignSelf="flex-start"
            />
            <VStack gap={1} align="flex-start" justify="center" flexGrow={1}>
              <Heading
                as="h2"
                className="theme-heading theme-heading-bold"
                fontSize="sm"
                color="blackAlpha.900"
                lineClamp={2}
                m={0}
                lineHeight="1.2em"
              >
                {clickTargetProps && enableLink !== false ? (
                  <Link to={pathLink} color="inherit" pos="relative" zIndex={2}>
                    {resource.name}
                  </Link>
                ) : (
                  resource.name
                )}
              </Heading>
              {
                // Moderators and Teachers should see list of Cohort members.
                [10, 15].includes(registration.role!) && (
                  <HStack gap={1}>
                    {/* <GroupAvatars
                    groupId={registration?.group?.id as number}
                    showGroupName={false}
                    lineClamp={1}
                    // @ts-ignore
                    fontColor="blackAlpha.900"
                  /> */}
                    <Pathicon
                      icon="group"
                      size=".85em"
                      color="blackAlpha.900"
                    />
                    <Text
                      as="span"
                      lineClamp={1}
                      fontSize="xs"
                      color="blackAlpha.900"
                    >
                      {registration?.group?.name}
                    </Text>
                  </HStack>
                )
              }

              <Box pos="relative" zIndex={2} _empty={{ display: "none" }}>
                <PathCardAction
                  registration={registration}
                  colorPalette="blackAlpha"
                  size="xs"
                />
              </Box>
            </VStack>
          </HStack>

          {!!nestedRegistrations?.length && (
            <Box ml={`calc(${imgWidth} + var(--chakra-spacing-2))`}>
              <NestedPaths
                nestedRegistrations={nestedRegistrations}
                fontSize=".5em"
                pos="relative"
                zIndex={2}
              />
            </Box>
          )}

          <RevealOnGroupHover
            as={HStack}
            gap={1}
            pos="absolute"
            top={1}
            right={1}
            zIndex={2}
          >
            {canSoftUnarchive && (
              <IconButton
                aria-label="Add to Home Screen"
                children={
                  <Pathicon
                    icon="sidebar-bottom"
                    transform="rotate(90deg)"
                    size="1em"
                  />
                }
                colorPalette="blackAlpha"
                size="2xs"
                cursor="pointer"
                onClick={() => softArchive()}
              />
            )}

            <PathCardRegistrationControls
              registration={registration}
              parentRegistration={parentRegistration}
              renderPortal
              triggerProps={{
                // Tricky to type props correctly to avoid requiring "aria-label" here.
                "aria-label": "Path Card Controls",
                colorPalette: "blackAlpha",
                size: "2xs"
              }}
            />
          </RevealOnGroupHover>
        </VStack>
      </Box>
    )
  }
)

export default PathCardListItem
